import React, { useEffect, useMemo, useState } from "react";
import { StatePanel, Tabs } from "../../components/ui";
import { daowatchesPreview, money, moneyIn, timeLabel, timeLeft } from "./dealsData";
import { DealCard, DealGrid, KpiStrip, Medians } from "./dealsParts";

const SOON_SECONDS = 6 * 3600;
const LONDON = "Europe/London";
const dayKey = (d) => d.toLocaleDateString("en-CA", { timeZone: LONDON });
const DAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: LONDON });
const WEEKDAY_TIME = new Intl.DateTimeFormat("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit", timeZone: LONDON });

// "Today" / "Tomorrow" / "Sat 31 Oct", in UK time.
const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 86400000);
  if (dayKey(d) === dayKey(today)) return "Today";
  if (dayKey(d) === dayKey(tomorrow)) return "Tomorrow";
  return DAY.format(d);
};

const byDay = (lots) => {
  const m = new Map();
  lots.forEach((w) => { const k = dayLabel(w.ends_at); if (!m.has(k)) m.set(k, []); m.get(k).push(w); });
  return [...m.entries()];
};

// One WatchCollecting lot, on the same card as Private sellers. The photo carries the countdown
// (red in the last hour, otherwise neutral); the bid's % under / over trade sits beside Trade 12M
// (`gap_pct` is the API's, vs the 12M trade median). The footer names the source.
const LotCard = ({ w, left }) => {
  const foreign = w.currency && w.currency !== "GBP";
  const bid = Number(w.current_bid) > 0;
  return (
    <DealCard
      label={[w.brand, w.model, w.reference].filter(Boolean).join(" ")}
      image={w.image}
      badges={<span className={`deals-cl__ends${left.tone === "hot" ? " deals-cl__ends--hot" : ""}`}>{left.label === "Ended" ? "Ended" : `${left.label} left`}</span>}
      reference={w.reference}
      refHref={daowatchesPreview(w.product_id)}
      name={`${w.brand || ""}${w.model ? ` · ${w.model}` : ""}`}
      specs={[w.year, `${w.bids ?? 0} bid${w.bids === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
      prices={<>
        <span className="deals-cl__line">
          {bid ? <strong>{money(w.current_bid_gbp ?? w.current_bid)}</strong> : <span className="deals-cl__muted">No bids yet</span>}
          {foreign && bid ? <span className="deals-cl__muted">{moneyIn(w.current_bid, w.currency)}</span> : null}
        </span>
        {/* Both medians side by side on every card ("—" when none), so cards line up and compare at a glance. */}
        <Medians trade={w.trade_median_12m} market={w.market_median_12m} gap={bid ? w.gap_pct : null} />
      </>}
      who="WatchCollecting"
      whoSub={<span className="deals-cl__who-phone">Closes {WEEKDAY_TIME.format(new Date(w.ends_at))}</span>}
      action={{ label: "Place bid", href: w.url || "#" }}
    />
  );
};

const Grid = ({ label, lots, leftOf }) => (
  <DealGrid label={label}>{lots.map((w) => <li key={w.id}><LotCard w={w} left={leftOf(w)} /></li>)}</DealGrid>
);

// WatchCollecting live lots: "Ending soon" (within 6h) and "All live", grouped by closing day.
// `seconds_left` comes from the API; a local 30 s tick keeps the countdowns moving between refreshes.
export default function AuctionsTab({ state, onRetry }) {
  const [view, setView] = useState("soon");
  const [loadedAt, setLoadedAt] = useState(() => Date.now());
  const [, tick] = useState(0);
  useEffect(() => { setLoadedAt(Date.now()); }, [state.data]);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const items = useMemo(() => state.data?.items || [], [state.data]);
  if (state.status === "loading") return <StatePanel state="loading" loadingShape="card" loadingCount={6} />;
  if (state.status === "error" && !state.data) return <StatePanel state="error" onRetry={onRetry} headingLevel={2} />;

  const elapsed = Math.floor((Date.now() - loadedAt) / 1000);
  const secondsOf = (w) => (w.seconds_left ?? 0) - elapsed;
  const leftOf = (w) => timeLeft(secondsOf(w));
  const live = items.filter((w) => secondsOf(w) > 0);
  const soon = live.filter((w) => secondsOf(w) <= SOON_SECONDS);
  const hour = soon.filter((w) => secondsOf(w) <= 3600).length;
  const underTrade = live.filter((w) => Number(w.gap_pct) > 0).length;

  // `feed` = when the box last uploaded; stale means the list can't be trusted.
  const feed = state.data?.feed;
  const feedDown = Boolean(feed?.stale);
  const status = !feed ? null : feedDown
    ? <span className="deals-feed deals-feed--down" role="status">{feed.last_seen_at ? `WatchCollecting feed stopped · last update ${timeLabel(feed.last_seen_at)}` : "WatchCollecting feed has never run"}</span>
    : <span className="deals-feed">Live from WatchCollecting · updated {timeLabel(feed.last_seen_at)}</span>;

  const lots = view === "soon" ? soon : live;
  const empty = feedDown ? "No live data: the WatchCollecting feed is not updating."
    : view === "soon" ? "Nothing closes in the next 6 hours." : "No live WatchCollecting auctions right now.";

  return (
    <div className="deals-stack">
      <KpiStrip
        tiles={[
          { tone: "danger", label: "Closing within 1h", value: hour },
          { tone: "purple", label: "Closing within 6h", value: soon.length },
          { tone: "default", label: "Live auctions", value: live.length },
          { tone: "success", label: "Bid under trade", value: underTrade },
        ]}
      />
      <div className="deals-section__head">
        <Tabs
          aria-label="Auctions"
          value={view}
          onChange={setView}
          items={[
            { value: "soon", label: "Ending soon", count: soon.length },
            { value: "all", label: "All live", count: live.length },
          ]}
        />
        {status}
      </div>
      {lots.length === 0 ? (
        <p className="deals-empty">{empty}</p>
      ) : view === "soon" ? (
        <Grid label="Auctions ending within 6 hours" lots={lots} leftOf={leftOf} />
      ) : (
        byDay(lots).map(([day, rows]) => (
          <section key={day} className="deals-brand" aria-label={`Closing ${day}`}>
            <div className="deals-brand__name">{day} <span className="deals-brand__count">{rows.length} lot{rows.length === 1 ? "" : "s"}</span></div>
            <Grid label={`Auctions closing ${day}`} lots={rows} leftOf={leftOf} />
          </section>
        ))
      )}
    </div>
  );
}
